import {
  useId,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import './TextInput.css';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Show a `N / max` counter below the field when maxLength is set. */
  showCount?: boolean;
  fieldClassName?: string;
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({
  label,
  hint,
  error,
  showCount,
  fieldClassName,
  id,
  className,
  disabled,
  required,
  value,
  maxLength,
  'aria-invalid': ariaInvalid,
  ...rest
}: TextareaProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const isInvalid = Boolean(error) || ariaInvalid === true || ariaInvalid === 'true';
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const currentLength = typeof value === 'string' ? value.length : undefined;

  return (
    <div
      className={clsx('fluent-field', fieldClassName)}
      data-invalid={isInvalid || undefined}
      data-disabled={disabled || undefined}
    >
      {label != null && (
        <label className="fluent-field__label" htmlFor={inputId}>
          {label}
          {required && (
            <span aria-hidden="true" style={{ color: 'var(--color-danger)', marginLeft: 2 }}>
              *
            </span>
          )}
        </label>
      )}
      <div className="fluent-field__frame">
        <textarea
          id={inputId}
          className={clsx('fluent-field__input', 'fluent-field__input--textarea', className)}
          disabled={disabled}
          required={required}
          value={value}
          maxLength={maxLength}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>
      {hint != null && !error && (
        <span id={hintId} className="fluent-field__hint">
          {hint}
        </span>
      )}
      {error != null && (
        <span id={errorId} className="fluent-field__error" role="alert">
          {error}
        </span>
      )}
      {showCount && maxLength != null && currentLength != null && (
        <span className="fluent-field__counter" aria-live="polite">
          {currentLength} / {maxLength}
        </span>
      )}
    </div>
  );
}
